import React, { FC, useState } from 'react';
import styled from 'styled-components';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { CircularLoader, FloatingButton, ImagePreviewDialog } from '@clatter/ui';
import { compareIdentifiers, useDeepCompareEffect } from '@clatter/platform';
import { useGetAssetThumbnailUrlQuery } from '../../../services';

const StyledImagePicker = styled.div<{
  $variant: string;
  $maxHeight: number;
  $maxWidth: number;
  $minHeight: number;
  $minWidth: number;
}>`
  ${({ $variant }) =>
    $variant === 'default' &&
    `
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    padding: 16px;
    overflow-y: auto;
  `}

  ${({ $variant }) =>
    $variant === 'carousel' &&
    `
    display: flex;
    gap: 16px;
    flex-wrap: nowrap;
    overflow-x: auto;
  `}

  .image-picker-item {
    cursor: pointer;
    position: relative;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    flex: 0 1 auto;
    width: auto;
    height: auto;

    .loader-container {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: ${(props) => props.$minWidth}px;
      min-height: ${(props) => props.$minHeight}px;
    }

    .image-picker-image {
      display: block;
      max-width: ${(props) => props.$maxWidth}px;
      max-height: ${(props) => props.$maxHeight}px;
      width: auto;
      height: auto;
      object-fit: contain;
    }

    .image-picker-image.loaded {
      opacity: 1;
    }

    .image-picker-actions {
      position: absolute;
      top: 8px;
      right: 8px;
    }

    .image-picker-details {
      display: flex;
      justify-content: center;
      align-items: center;
      line-height: 32px;
      padding: 0 8px;
      font-size: 12px;

      .file-title {
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .image-selected {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 20px;
      height: 20px;
      background-color: ${({ theme }) => theme.palette.secondaryColor};
      display: flex;
      justify-content: center;
      align-items: center;

      svg {
        color: #fff;
        font-size: 20px;
      }
    }

    &.image-picker-item-selected {
      .image-picker-image {
        outline: 4px solid ${({ theme }) => theme.palette.primaryColor};
      }
    }
  }
`;

interface ImagePickerProps {
  images: { id: string | number; url: string; name: string }[];
  value: any;
  onChange: (data: any) => void;
  required?: boolean;
  multiple?: boolean;
  urlProperty?: string;
  titleProperty?: string;
  renderItemActions?: any;
  fetchPresignedThumbnail?: boolean;
  variant: 'default' | 'carousel';
  requiredIds?: (string | number)[];
  maxImageHeight?: number;
  maxImageWidth?: number;
  minImageHeight?: number;
  minImageWidth?: number;
}

const RenderImages = ({
  images = [],
  titleProperty,
  urlProperty,
  onClick,
  onPreviewClick,
  isItemSelected,
  renderItemActions,
  fetchPresignedThumbnail,
  maxImageHeight = 240,
  maxImageWidth = 140,
}: {
  images: any[];
  titleProperty: string;
  urlProperty: string;
  isItemSelected: any;
  renderItemActions: any;
  onClick: (data: any) => void;
  onPreviewClick: (imageUrl: string) => void;
  fetchPresignedThumbnail: boolean;
  maxImageHeight?: number;
  maxImageWidth?: number;
}) => {
  const handlePreview = (e: any, imageUrl: string) => {
    e.stopPropagation();
    onPreviewClick(imageUrl);
  };

  if (!images || !images.length) {
    return <p>No images to pick</p>;
  }

  return images.map((image) => {
    const { data: presignedThumbnailUrl } = useGetAssetThumbnailUrlQuery(
      { assetId: image.id! },
      { skip: !image?.id || !fetchPresignedThumbnail },
    );

    const imageUrl = fetchPresignedThumbnail ? presignedThumbnailUrl : get(image, urlProperty);

    const [loaded, setLoaded] = useState(false);

    return (
      <div
        data-id={image?.id}
        onClick={onClick}
        className={`image-picker-item ${isItemSelected(image?.id) ? 'image-picker-item-selected' : ''}`}
        key={image?.id}
      >
        <FloatingButton
          className="gallery-image-preview-button"
          onClick={(e: any) => handlePreview(e, imageUrl)}
          // @ts-ignore
          top={5}
          right={6}
        >
          <VisibilityOutlinedIcon />
        </FloatingButton>

        {!loaded && (
          <div className="loader-container">
            <CircularLoader />
          </div>
        )}

        <img
          src={imageUrl}
          alt={get(image, titleProperty)}
          className={`image-picker-image ${loaded ? 'loaded' : ''}`}
          style={{ display: loaded ? 'block' : 'none' }}
          onLoad={() => setLoaded(true)}
        />

        {typeof renderItemActions === 'function' && (
          <div className="image-picker-actions">{renderItemActions(image)}</div>
        )}
        <div className="image-picker-details">
          <div className="file-title">{get(image, titleProperty)}</div>
        </div>
      </div>
    );
  });
};

export const ImagePicker: FC<ImagePickerProps> = ({
  images = [],
  multiple = false,
  onChange,
  value,
  urlProperty = 'url',
  titleProperty = 'name',
  renderItemActions,
  fetchPresignedThumbnail = false,
  required = false,
  variant = 'default',
  requiredIds,
  maxImageHeight = 140,
  maxImageWidth = 260,
  minImageHeight = 90,
  minImageWidth = 120,
}) => {
  const [previewImage, setPreviewImage] = useState<any>(null);

  const isItemSelected = (imageId: any) => {
    if (!value) return false;
    if (
      (!multiple && compareIdentifiers(value.id, imageId)) ||
      (multiple && value.length && value.some((image: any) => compareIdentifiers(image.id, imageId)))
    ) {
      return true;
    }
    return false;
  };

  const handleImageClick = (event: any) => {
    const clickedItemId = parseInt(event.currentTarget.dataset.id);
    const clickedImage = images.find((img) => compareIdentifiers(img.id, clickedItemId));
    let nextValue;

    if (multiple) {
      nextValue = cloneDeep(value) || [];
      if (nextValue.some((image: any) => compareIdentifiers(image.id, clickedItemId))) {
        if (requiredIds?.some((id) => compareIdentifiers(id, clickedItemId))) return;
        nextValue = nextValue.filter((image: any) => !compareIdentifiers(image.id, clickedItemId));
      } else {
        nextValue.push(clickedImage);
      }
    } else {
      if (value && compareIdentifiers(value.id, clickedItemId)) {
        if (required) return;
        nextValue = null;
      } else {
        nextValue = clickedImage;
      }
    }

    typeof onChange === 'function' && onChange(nextValue);
  };

  useDeepCompareEffect(() => {
    if (!value?.id && images?.length > 0 && required) {
      onChange(images[0]);
    }
  }, [images, required, value]);

  return (
    <StyledImagePicker
      $variant={variant}
      $maxHeight={maxImageHeight}
      $maxWidth={maxImageWidth}
      $minHeight={minImageHeight}
      $minWidth={minImageWidth}
    >
      {/*@ts-ignore*/}
      <RenderImages
        images={images}
        urlProperty={urlProperty}
        titleProperty={titleProperty}
        renderItemActions={renderItemActions}
        fetchPresignedThumbnail={fetchPresignedThumbnail}
        onClick={handleImageClick}
        onPreviewClick={setPreviewImage}
        isItemSelected={isItemSelected}
        maxImageHeight={maxImageHeight}
        maxImageWidth={maxImageWidth}
      />

      <ImagePreviewDialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage}
        alt="Full preview"
      />
    </StyledImagePicker>
  );
};
